import React, { useState } from "react";
import InputFile from "../../atoms/InputFile/InputFile";
import Loader from "../../atoms/Loader/Loader";
import { CrossIcon } from "./asset/icon";
import styles from "./FileListManager.module.scss";
import { ImageViewer, ImageData } from "../../organismes/ImageViewer";
import EmptyListIcon from "../../atoms/EmptyListIcon/EmptyListIcon";
import { PdfIcon, ExcelIcon } from "../../../assets/JWayIcons";


export type FileUrl = File & { url?: string, toNotUpload?: boolean, onDelete?: (file: FileUrl) => void };



export interface FileListManagerProps {
  listFiles?: FileUrl[];
  setFileList?: (files: FileUrl[]) => void;
  messageEmpty?: string;
  limitFiles?: number;
  addLabel?: string;
  noAddFile?: boolean;
  label?: string;
  accept?: string;
  isError?: boolean;
  isLoading?: boolean;
}
export default function FileListManager({
  listFiles,
  setFileList,
  messageEmpty = "Aucun fichier",
  limitFiles = 1,
  addLabel = "Ajouter un fichier",
  noAddFile,
  label = "Fichiers",
  accept,
  isError,
  isLoading,
}: FileListManagerProps) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handleDeleteFile = (file: FileUrl) => {
    if (listFiles && listFiles.length > 0) {
      const newFiles = listFiles.filter((f) => f !== file);
      setFileList && setFileList(newFiles);
    }
  };

  const handleAddFile = (file:  File[]) => {
    const newFiles = [...(listFiles || []), ...file];
    setFileList && setFileList(newFiles);
  };

  // Helper function to check if file is an image
  const isImageFile = (file: FileUrl) => {
    const fileName = file.name.toLowerCase();
    const hasImageExtension = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileName);
    const hasImageMimeType = file.type?.includes('image/');
    return hasImageExtension || hasImageMimeType;
  };

  // Helper function to get file type for display
  const getFileType = (file: FileUrl): 'image' | 'pdf' | 'excel' | 'other' => {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type?.toLowerCase() || '';
    
    // Check for images
    if (isImageFile(file)) {
      return 'image';
    }
    
    // Check for PDF
    if (fileName.endsWith('.pdf') || mimeType.includes('pdf')) {
      return 'pdf';
    }
    
    // Check for Excel files
    if (
      fileName.endsWith('.xlsx') || 
      fileName.endsWith('.xls') || 
      fileName.endsWith('.xlsm') || 
      fileName.endsWith('.xlsb') ||
      mimeType.includes('spreadsheet') ||
      mimeType.includes('excel') ||
      mimeType.includes('vnd.ms-excel') ||
      mimeType.includes('vnd.openxmlformats-officedocument.spreadsheetml')
    ) {
      return 'excel';
    }
    
    return 'other';
  };

  // Filter images for the viewer
  const imageFiles: ImageData[] = (listFiles || [])
    .filter(isImageFile)
    .map((file, index) => ({
      id: `file-${index}`,
      src: file.url || URL.createObjectURL(file),
      name: file.name,
      type: file.type || 'image'
    }));

  // Handle image click to open viewer
  const handleImageClick = (file: FileUrl) => {
    if (!isImageFile(file)) return;
    
    const imageIndex = imageFiles.findIndex(img => img.name === file.name);
    if (imageIndex !== -1) {
      setCurrentImageIndex(imageIndex);
      setViewerOpen(true);
    }
  };

  return (
    <div className={styles["global-container"]}>
      <label className={[styles["global-container__label"],isError ? styles["global-container__label--error"] : ''].join(" ")}>{label}</label>
      <div
        className={[
          styles["file-list-container"],
          isError ? styles["file-list-container--error"] : "",
        ].join(" ")}
      >
        <div className={styles["file-container"]}>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <Loader size={24} centered />
            </div>
          ) : listFiles && listFiles.length > 0 ? (
            listFiles?.map((file, index) => (
              <FileItem
                key={index}
                file={file}
                fileType={getFileType(file)}
                // Persisted files (toNotUpload) can only be removed via an explicit onDelete
                // (e.g. admin password-gated deletion); local, not-yet-uploaded files remain
                // freely removable via the generic local-list handler.
                onFileRemove={file.onDelete ? file.onDelete : (file.toNotUpload ? undefined : handleDeleteFile)}
                onFileClick={(file) => isImageFile(file) ? handleImageClick(file) : undefined}
              />
            ))
          ) : (
            <div className={[styles["file-list-container__empty-state"], isError ? styles['file-list-container__empty-state--error']: ''].join(" ")}>
              <EmptyListIcon message={messageEmpty} marginTop="40px" />
            </div>
          )}
        </div>
        {!noAddFile &&
          limitFiles &&
          listFiles &&
          listFiles.length < limitFiles && (
            <div className={styles["add-file-btn-container"]}>
              <InputFile
                addFiles={handleAddFile}
                label={addLabel}
                accept={accept}
                isError={isError}
              />
            </div>
          )}
      </div>

      {/* Image Viewer Modal */}
      {imageFiles.length > 0 && (
        <ImageViewer
          images={imageFiles}
          currentIndex={currentImageIndex}
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
          onNavigate={setCurrentImageIndex}
        />
      )}


    </div>
  );
}

export const FileItem = ({
  file,
  fileType,
  onFileClick,
  onFileRemove,
}: {
  file: FileUrl;
  fileType: 'image' | 'pdf' | 'excel' | 'other';
  onFileClick?: (file: FileUrl) => void;
  onFileRemove?: (file: FileUrl) => void;
}) => {
  const isImage = fileType === 'image';

  return (
    <div className={styles["container-file-item"]}>
      <div className={styles["container-file-item__delete-btn"]}>
        {onFileRemove && (
          <DeleteFileBtn file={file} onFileRemove={onFileRemove} />
        )}
      </div>
      <div 
        className={`${styles["file-item"]} ${isImage && onFileClick ? styles["file-item--clickable"] : ""}`}
        onClick={() => onFileClick && onFileClick(file)}
      >
        {isImage ? (
          <>
        
            <img
              className={styles["file-item__img"]}
              src={file.url || URL.createObjectURL(file)}
              alt={file.name}
            />
            <div className={styles["file-item__overlay"]}>
              <span className={styles["file-item__name"]}>
                {file.name}
              </span>
            </div>
            <div className={styles["file-item__overlay-hover"]}>
              <span className={styles["file-item__name-full"]}>
                {file.name}
              </span>
            </div>
          </>
        ) : (
          <div className={styles["file-item__icon-container"]}>
            <div className={styles["file-item__icon"]}>
              {fileType === 'pdf' && <PdfIcon size={50} />}
              {fileType === 'excel' && <ExcelIcon size={50} />}
              {fileType === 'other' && (
                <div className={styles["file-item__default-icon"]}>
                  📄
                </div>
              )}
            </div>
            <span className={styles["file-item__name"]} title={file.name}>
              {file.name}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const DeleteFileBtn = ({
  file,
  onFileRemove,
}: {
  file: File;
  onFileRemove?: (file: FileUrl) => void;
}) => {
  return (
    <button
      className={styles["delete-btn"]}
      onClick={(e) =>{ e.preventDefault(); onFileRemove && onFileRemove(file)}}
    >
      <CrossIcon />
    </button>
  );
};
